package com.partejar.app.feature.equipe.presentation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarSkeleton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.components.clearFocusOnTapOutside
import com.partejar.app.core.ui.components.rememberBringIntoViewOnFocusModifier
import com.partejar.app.core.ui.components.rememberFocusedFieldBoundsModifier
import com.partejar.app.core.ui.components.rememberTapOutsideFocusState
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.core.security.BiometricAvailability
import com.partejar.app.core.security.rememberBiometricAuthenticator
import com.partejar.app.core.session.SessionDestination
import com.partejar.app.feature.equipe.data.EquipeMember
import com.partejar.app.feature.equipe.data.TeamInviteInput
import com.partejar.app.feature.equipe.data.TeamMemberUpdate
import com.partejar.app.feature.entitlements.data.CommercialState
import com.partejar.app.feature.entitlements.data.WorkspaceEntitlement
import com.partejar.app.feature.workspace.data.UserWorkspace
import com.partejar.app.feature.workspace.presentation.WorkspaceViewModel
import org.koin.compose.koinInject

@Composable
fun EquipeScreen(
    onBack: () -> Unit,
    onWorkspaces: () -> Unit = {},
    onEditWorkspace: (Long) -> Unit = {},
    onSessionDestination: (SessionDestination) -> Unit = {},
    viewModel: EquipeViewModel = koinInject(),
    workspaceViewModel: WorkspaceViewModel = koinInject()
) {
    val state by viewModel.uiState.collectAsState()
    val action by viewModel.actionState.collectAsState()
    val registrations by viewModel.registrations.collectAsState()
    val commercialState = viewModel.commercialState?.collectAsState()?.value
    val session by workspaceViewModel.bootstrap.collectAsState()
    val workspace = workspaceViewModel.currentWorkspace()
    val authenticator = rememberBiometricAuthenticator()
    var mode by remember { mutableStateOf<EquipeMode>(EquipeMode.List) }

    LaunchedEffect(action) {
        when (val current = action) {
            is EquipeActionState.InviteCreated -> mode = EquipeMode.List
            is EquipeActionState.MemberUpdated -> {
                mode = EquipeMode.List
                if (current.member.userId == session?.usuario?.id) {
                    workspaceViewModel.refreshSession(onSessionDestination)
                }
            }
            is EquipeActionState.MemberRemoved -> {
                mode = EquipeMode.List
                if (current.userId == session?.usuario?.id) {
                    workspaceViewModel.refreshSession(onSessionDestination)
                }
            }
            else -> Unit
        }
    }

    when (val current = mode) {
        EquipeMode.List -> WorkspaceAdministrationLayout(
            workspace = workspace,
            currentUserId = session?.usuario?.id,
            state = state,
            action = action,
            commercialState = commercialState,
            onBack = onBack,
            onInvite = { mode = EquipeMode.Invite },
            onMember = { mode = EquipeMode.Edit(it) },
            onEditWorkspace = { workspace?.id?.let(onEditWorkspace) },
            onWorkspaces = onWorkspaces,
            onRetry = viewModel::load,
            onClearAction = viewModel::clearAction
        )
        EquipeMode.Invite -> InviteForm(
            action = action,
            onBack = { viewModel.clearAction(); mode = EquipeMode.List },
            onSubmit = viewModel::invite,
            onClearAction = viewModel::clearAction
        )
        is EquipeMode.Edit -> {
            LaunchedEffect(current.member.id) { viewModel.loadRegistrations(current.member.id) }
            EditMemberForm(
            member = current.member,
            isCurrentUser = current.member.userId == session?.usuario?.id,
            registrations = registrations,
            action = action,
            authenticationAvailability = authenticator.availability(),
            onBack = { viewModel.clearAction(); mode = EquipeMode.List },
            onSubmit = { update -> viewModel.update(current.member, update) },
            onRemove = {
                viewModel.remove(current.member) {
                    authenticator.authenticate(
                        title = "Remover integrante",
                        reason = "Confirme sua identidade para remover ${current.member.name} deste espaço."
                    )
                }
            },
            onClearAction = viewModel::clearAction
            )
        }
    }
}

private sealed interface EquipeMode {
    data object List : EquipeMode
    data object Invite : EquipeMode
    data class Edit(val member: EquipeMember) : EquipeMode
}

@Composable
private fun WorkspaceAdministrationLayout(
    workspace: UserWorkspace?,
    currentUserId: Long?,
    state: EquipeUiState,
    action: EquipeActionState,
    commercialState: CommercialState<WorkspaceEntitlement>?,
    onBack: () -> Unit,
    onInvite: () -> Unit,
    onMember: (EquipeMember) -> Unit,
    onEditWorkspace: () -> Unit,
    onWorkspaces: () -> Unit,
    onRetry: () -> Unit,
    onClearAction: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val canManageTeam = workspace?.perfil?.codigo == "ADMINISTRADOR"
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            PartejarTopBar(
                title = workspace?.nome ?: "Espaço",
                subtitle = "Administração do espaço",
                leadingContent = { BackAction(onBack) },
            )
        }
    ) { padding ->
        when (state) {
            EquipeUiState.Loading -> Loading(Modifier.padding(padding))
            is EquipeUiState.Error -> ErrorContent(
                state.message,
                onRetry,
                Modifier.padding(padding).padding(spacing.space24)
            )
            is EquipeUiState.Content -> LazyColumn(
                modifier = Modifier.fillMaxSize().padding(padding),
                contentPadding = PaddingValues(spacing.space16),
                verticalArrangement = Arrangement.spacedBy(spacing.space16)
            ) {
                item {
                    WorkspaceSectionTitle("Geral")
                }
                item {
                    WorkspaceActionRow(
                        icon = PartejarIcons.Navigation.workspace,
                        title = workspace?.nome ?: "Espaço indisponível",
                        subtitle = workspace?.perfil?.nome,
                        actionLabel = if (workspace?.canEdit == true) "Editar nome" else null,
                        onClick = if (workspace?.canEdit == true) onEditWorkspace else null
                    )
                }

                item {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column(Modifier.weight(1f)) {
                            WorkspaceSectionTitle("Equipe")
                            val activeCount = state.members.count(EquipeMember::active)
                            Text(
                                "$activeCount ${if (activeCount == 1) "integrante" else "integrantes"}",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                        if (canManageTeam) {
                            TextButton(onClick = onInvite) {
                                Icon(
                                    PartejarIcons.Actions.add,
                                    contentDescription = null,
                                    modifier = Modifier.size(PartejarTheme.dimensions.iconMedium)
                                )
                                Text("Convidar")
                            }
                        }
                    }
                }
                if (canManageTeam) commercialState?.let { state ->
                    when (state) {
                        is CommercialState.Available -> state.value.memberLimit?.let { limit ->
                            item {
                                PartejarAlert(
                                    title = "Uso da equipe: ${state.value.membersUsed}/$limit",
                                    tone = if (state.value.membersUsed >= limit) PartejarAlertTone.ATTENTION else PartejarAlertTone.INFO
                                ) {
                                    Text(if (state.value.membersUsed >= limit) "O limite pode impedir novos convites." else "Convites válidos também consomem vagas até serem aceitos ou expirarem.")
                                }
                            }
                        }
                        is CommercialState.TemporarilyUnavailable -> item {
                            PartejarAlert(title = "Limite da equipe indisponível", tone = PartejarAlertTone.INFO) {
                                Text("Ainda é possível tentar convidar; a API confirmará a disponibilidade.")
                            }
                        }
                        CommercialState.Loading -> Unit
                    }
                }
                if (action is EquipeActionState.InviteCreated) {
                    item {
                        InviteSuccess(action, onClearAction)
                    }
                }
                if (action is EquipeActionState.MemberUpdated) item {
                    SuccessFeedback("Alterações salvas", "O vínculo no espaço foi atualizado.", onClearAction)
                }
                if (action is EquipeActionState.MemberRemoved) item {
                    SuccessFeedback("Integrante removida", "O acesso a este espaço foi encerrado.", onClearAction)
                }
                val activeMembers = state.members.filter(EquipeMember::active)
                if (activeMembers.isEmpty()) {
                    item { EmptyContent() }
                } else {
                    items(activeMembers, key = { it.id }) { member ->
                        TeamMemberCard(
                            member = member,
                            isCurrentUser = member.userId == currentUserId,
                            enabled = canManageTeam,
                            onClick = { onMember(member) }
                        )
                    }
                }
                if (activeMembers.size == 1 && activeMembers.single().userId == currentUserId) item {
                    Text(
                        "Só você faz parte deste espaço por enquanto.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                item { WorkspaceSectionTitle("Espaços") }
                item {
                    WorkspaceActionRow(
                        icon = PartejarIcons.Navigation.workspace,
                        title = "Trocar de workspace",
                        subtitle = "Selecionar outro espaço ou criar um novo",
                        actionLabel = "Abrir",
                        onClick = onWorkspaces
                    )
                }
            }
        }
    }
}

@Composable
private fun WorkspaceSectionTitle(title: String) {
    Text(
        text = title,
        modifier = Modifier.semantics { heading() },
        style = MaterialTheme.typography.labelLarge,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
}

@Composable
private fun WorkspaceActionRow(
    icon: ImageVector,
    title: String,
    subtitle: String?,
    actionLabel: String?,
    onClick: (() -> Unit)?
) {
    val spacing = PartejarTheme.spacing
    val content: @Composable () -> Unit = {
        Row(
            modifier = Modifier.fillMaxWidth().padding(spacing.space16),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
            Column(Modifier.weight(1f).padding(horizontal = spacing.space12)) {
                Text(title, style = MaterialTheme.typography.titleMedium, maxLines = 2, overflow = TextOverflow.Ellipsis)
                subtitle?.let {
                    Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            }
            actionLabel?.let {
                Text(it, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.primary)
            }
            if (onClick != null) {
                Icon(
                    PartejarIcons.Actions.next,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(start = spacing.space8)
                )
            }
        }
    }
    if (onClick != null) {
        Surface(
            onClick = onClick,
            modifier = Modifier.fillMaxWidth().heightIn(min = PartejarTheme.dimensions.controlHeight),
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.surfaceContainerLow,
            content = content
        )
    } else {
        Surface(
            modifier = Modifier.fillMaxWidth().heightIn(min = PartejarTheme.dimensions.controlHeight),
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.surfaceContainerLow,
            content = content
        )
    }
}

@Composable
private fun InviteSuccess(action: EquipeActionState.InviteCreated, onClear: () -> Unit) {
    val clipboard = LocalClipboardManager.current
    PartejarAlert(title = "Convite criado", tone = PartejarAlertTone.SUCCESS) {
        Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
            Text("Envie este link à pessoa convidada. Ele expira em ${action.result.expiresAt}.")
            SelectionContainer { Text(action.result.link, style = MaterialTheme.typography.bodySmall) }
            PartejarButton(
                text = "Copiar link",
                onClick = { clipboard.setText(AnnotatedString(action.result.link)) },
                variant = PartejarButtonVariant.SECONDARY
            )
            TextButton(onClick = onClear) { Text("Dispensar") }
        }
    }
}

@Composable
private fun SuccessFeedback(title: String, message: String, onClear: () -> Unit) {
    PartejarAlert(title = title, tone = PartejarAlertTone.SUCCESS) {
        Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
            Text(message)
            TextButton(onClick = onClear) { Text("Dispensar") }
        }
    }
}

@Composable
private fun TeamMemberCard(
    member: EquipeMember,
    isCurrentUser: Boolean,
    enabled: Boolean,
    onClick: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val content: @Composable () -> Unit = {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                PartejarIcons.Navigation.equipe,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.padding(start = spacing.space16)
            )
            Column(Modifier.weight(1f).padding(spacing.space16)) {
                Text(member.name, style = MaterialTheme.typography.titleMedium, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(member.email, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(
                    listOfNotNull(member.profileName, categoryLabel(member.professionalCategory)).joinToString(" · "),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                if (isCurrentUser) Text("Você", style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.primary)
            }
            if (enabled) Icon(
                PartejarIcons.Actions.next,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(end = spacing.space16)
            )
        }
    }
    if (enabled) {
        Surface(
            onClick = onClick,
            modifier = Modifier.fillMaxWidth().heightIn(min = PartejarTheme.dimensions.controlHeight),
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.surfaceContainerLow,
            contentColor = MaterialTheme.colorScheme.onSurface,
            content = content
        )
    } else {
        Surface(
            modifier = Modifier.fillMaxWidth().heightIn(min = PartejarTheme.dimensions.controlHeight),
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.surfaceContainerLow,
            contentColor = MaterialTheme.colorScheme.onSurface,
            content = content
        )
    }
}

@Composable
private fun InviteForm(
    action: EquipeActionState,
    onBack: () -> Unit,
    onSubmit: (TeamInviteInput) -> Unit,
    onClearAction: () -> Unit
) {
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var profileCode by remember { mutableStateOf("EQUIPE_CLINICA") }
    var category by remember { mutableStateOf("DOULA") }
    val focusManager = LocalFocusManager.current
    val emailFocus = remember { FocusRequester() }
    val tapOutsideFocus = rememberTapOutsideFocusState()
    val nameFocusModifier = rememberFocusedFieldBoundsModifier(tapOutsideFocus)
        .then(rememberBringIntoViewOnFocusModifier(action))
    val emailFocusModifier = Modifier
        .focusRequester(emailFocus)
        .then(rememberFocusedFieldBoundsModifier(tapOutsideFocus))
        .then(rememberBringIntoViewOnFocusModifier(action))
    TeamFormScaffold("Convidar pessoa", onBack, tapOutsideFocus) {
        PartejarTextField(
            name,
            { name = it },
            "Nome",
            modifier = Modifier.then(nameFocusModifier),
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
            keyboardActions = KeyboardActions(onNext = { emailFocus.requestFocus() })
        )
        PartejarTextField(
            email,
            { email = it },
            "E-mail",
            modifier = Modifier.then(emailFocusModifier),
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
            keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus() })
        )
        ProfileSelector(profileCode, { profileCode = it })
        CategorySelector(category, { category = it })
        ActionFeedback(action, onClearAction)
        PartejarButton(
            text = "Gerar convite",
            onClick = { onSubmit(TeamInviteInput(name, email, profileCode, category)) },
            isLoading = action is EquipeActionState.Submitting,
            enabled = name.isNotBlank() && email.isNotBlank()
        )
    }
}

@Composable
private fun EditMemberForm(
    member: EquipeMember,
    isCurrentUser: Boolean,
    registrations: List<com.partejar.app.feature.equipe.data.ProfessionalRegistration>,
    action: EquipeActionState,
    authenticationAvailability: BiometricAvailability,
    onBack: () -> Unit,
    onSubmit: (TeamMemberUpdate) -> Unit,
    onRemove: () -> Unit,
    onClearAction: () -> Unit
) {
    var profileCode by remember(member.id) { mutableStateOf(member.profileCode) }
    var category by remember(member.id) { mutableStateOf(member.professionalCategory) }
    var registrationId by remember(member.id) { mutableStateOf(member.selectedProfessionalRegistration?.id) }
    var showRemovalConfirmation by remember(member.id) { mutableStateOf(false) }
    TeamFormScaffold("Editar integrante", onBack) {
        Text(member.name, style = MaterialTheme.typography.titleMedium)
        Text(member.email, color = MaterialTheme.colorScheme.onSurfaceVariant)
        if (isCurrentUser) Text("Você", style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.primary)
        ProfileSelector(profileCode, { profileCode = it })
        CategorySelector(category, { category = it })
        RegistrationSelector(registrations, registrationId, { registrationId = it })
        ActionFeedback(action, onClearAction)
        PartejarButton(
            text = "Salvar alterações",
            onClick = { onSubmit(TeamMemberUpdate(profileCode, category, registrationId, active = true)) },
            isLoading = action is EquipeActionState.Submitting
        )
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        Text(
            "Ações sensíveis",
            modifier = Modifier.semantics { heading() },
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        if (authenticationAvailability != BiometricAvailability.AVAILABLE) {
            PartejarAlert(title = "Confirmação segura indisponível", tone = PartejarAlertTone.ATTENTION) {
                Text("Configure biometria, PIN, padrão ou senha no dispositivo para remover integrantes.")
            }
        }
        PartejarButton(
            text = if (isCurrentUser) "Sair deste workspace" else "Remover do workspace",
            onClick = {
                if (authenticationAvailability == BiometricAvailability.AVAILABLE) {
                    showRemovalConfirmation = true
                }
            },
            isLoading = action is EquipeActionState.Authenticating,
            enabled = authenticationAvailability == BiometricAvailability.AVAILABLE,
            variant = PartejarButtonVariant.DESTRUCTIVE_SECONDARY
        )
    }
    if (showRemovalConfirmation) {
        AlertDialog(
            onDismissRequest = { showRemovalConfirmation = false },
            title = {
                Text(if (isCurrentUser) "Sair deste workspace?" else "Remover ${member.name} do workspace?")
            },
            text = {
                Text(
                    if (isCurrentUser) {
                        "Você perderá acesso aos dados deste espaço. Sua conta não será excluída."
                    } else {
                        "Esta pessoa perderá acesso aos dados deste espaço. A conta profissional não será excluída."
                    }
                )
            },
            confirmButton = {
                TextButton(onClick = {
                    showRemovalConfirmation = false
                    onRemove()
                }) { Text(if (isCurrentUser) "Sair" else "Remover") }
            },
            dismissButton = {
                TextButton(onClick = { showRemovalConfirmation = false }) { Text("Cancelar") }
            }
        )
    }
}

@Composable
private fun RegistrationSelector(registrations: List<com.partejar.app.feature.equipe.data.ProfessionalRegistration>, selected: Long?, onSelected: (Long?) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
        Text("Registro profissional usado neste espaço", style = MaterialTheme.typography.labelLarge)
        PartejarButton("Não selecionar registro", { onSelected(null) }, variant = if (selected == null) PartejarButtonVariant.PRIMARY else PartejarButtonVariant.SECONDARY)
        registrations.forEach { registration ->
            PartejarButton(registration.label, { onSelected(registration.id) }, variant = if (selected == registration.id) PartejarButtonVariant.PRIMARY else PartejarButtonVariant.SECONDARY)
        }
        if (registrations.isEmpty()) Text("Nenhum registro profissional ativo cadastrado para esta pessoa.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
    }
}

@Composable
private fun TeamFormScaffold(
    title: String,
    onBack: () -> Unit,
    tapOutsideFocus: com.partejar.app.core.ui.components.TapOutsideFocusState? = null,
    content: @Composable () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val focusDismissModifier = tapOutsideFocus?.let { Modifier.clearFocusOnTapOutside(it) } ?: Modifier
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = { PartejarTopBar(title, leadingContent = { BackAction(onBack) }) }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .then(focusDismissModifier)
                .imePadding()
                .verticalScroll(rememberScrollState())
                .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
                .padding(spacing.space24),
            verticalArrangement = Arrangement.spacedBy(spacing.space16),
            content = { content() }
        )
    }
}

@Composable
private fun ProfileSelector(selected: String, onSelected: (String) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
        Text("Perfil", style = MaterialTheme.typography.labelLarge)
        Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
            PartejarButton("Equipe clínica", { onSelected("EQUIPE_CLINICA") }, modifier = Modifier.weight(1f), variant = if (selected == "EQUIPE_CLINICA") PartejarButtonVariant.PRIMARY else PartejarButtonVariant.SECONDARY)
            PartejarButton("Administradora", { onSelected("ADMINISTRADOR") }, modifier = Modifier.weight(1f), variant = if (selected == "ADMINISTRADOR") PartejarButtonVariant.PRIMARY else PartejarButtonVariant.SECONDARY)
        }
    }
}

@Composable
private fun CategorySelector(selected: String, onSelected: (String) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
        Text("Categoria profissional", style = MaterialTheme.typography.labelLarge)
        Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
            CategoryButton("DOULA", selected, onSelected)
            CategoryButton("ENFERMEIRA_OBSTETRICA", selected, onSelected)
        }
        Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
            CategoryButton("MEDICO", selected, onSelected)
            CategoryButton("OUTRO", selected, onSelected)
        }
    }
}

@Composable
private fun androidx.compose.foundation.layout.RowScope.CategoryButton(category: String, selected: String, onSelected: (String) -> Unit) = PartejarButton(
    text = categoryLabel(category),
    onClick = { onSelected(category) },
    modifier = Modifier.weight(1f),
    variant = if (selected == category) PartejarButtonVariant.PRIMARY else PartejarButtonVariant.SECONDARY
)

private fun categoryLabel(value: String): String = when (value) {
    "ENFERMEIRA_OBSTETRICA" -> "Enfermeira obstétrica"
    "DOULA" -> "Doula"
    "MEDICO" -> "Médico"
    else -> "Outro"
}

@Composable
private fun ActionFeedback(action: EquipeActionState, onClear: () -> Unit) {
    when (action) {
        is EquipeActionState.Error -> {
            PartejarAlert(title = "Não foi possível concluir", tone = PartejarAlertTone.ERROR) {
                Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                    Text(action.message)
                    TextButton(onClick = onClear) { Text("Dispensar") }
                }
            }
        }
        EquipeActionState.AuthenticationCancelled -> {
            PartejarAlert(title = "Confirmação cancelada", tone = PartejarAlertTone.INFO) {
                Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                    Text("Nenhuma alteração foi realizada.")
                    TextButton(onClick = onClear) { Text("Dispensar") }
                }
            }
        }
        else -> Unit
    }
}

@Composable
private fun ErrorContent(message: String, onRetry: () -> Unit, modifier: Modifier) = Column(modifier, verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)) {
    PartejarAlert(title = "Equipe indisponível", tone = PartejarAlertTone.ERROR) { Text(message) }
    PartejarButton("Tentar novamente", onRetry)
}

@Composable
private fun EmptyContent() = PartejarAlert(title = "Nenhuma pessoa cadastrada", tone = PartejarAlertTone.INFO) {
    Text("Convide a primeira pessoa para este espaço.")
}

@Composable
private fun Loading(modifier: Modifier) = LazyColumn(
    modifier = modifier.fillMaxSize(),
    contentPadding = PaddingValues(PartejarTheme.spacing.space16),
    verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)
) {
    items(3) {
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)) {
                PartejarSkeleton(Modifier.height(PartejarTheme.spacing.space40).weight(0.18f))
                Column(Modifier.weight(0.82f), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.62f).height(PartejarTheme.spacing.space16))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.86f).height(PartejarTheme.spacing.space12))
                }
            }
        }
    }
}

@Composable
private fun BackAction(onClick: () -> Unit) = IconButton(onClick = onClick) {
    Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
}

private val previewMembers = listOf(
    EquipeMember(1, 1, "Ana Souza", "ana@partejar.local", "ADMINISTRADOR", "Administradora", "DOULA", active = true),
    EquipeMember(2, 2, "Lis Martins", "lis@partejar.local", "EQUIPE_CLINICA", "Equipe clínica", "ENFERMEIRA_OBSTETRICA", active = true),
    EquipeMember(3, 3, "Clara Lima", "clara@partejar.local", "EQUIPE_CLINICA", "Equipe clínica", "MEDICO", active = false)
)

private val previewWorkspace = UserWorkspace(
    id = 1,
    nome = "Clínica Aurora",
    perfil = com.partejar.app.feature.workspace.data.WorkspaceProfile("ADMINISTRADOR", "Administrador"),
    categoriaProfissional = "DOULA",
    usuarioAtualEhProprietario = true
)

@Preview(name = "Equipe · Light", widthDp = 390, heightDp = 844)
@Composable
private fun EquipeLightPreview() = PartejarTheme(themeMode = ThemeMode.LIGHT) {
    WorkspaceAdministrationLayout(previewWorkspace, 1, EquipeUiState.Content(previewMembers), EquipeActionState.Idle, null, {}, {}, {}, {}, {}, {}, {})
}

@Preview(name = "Equipe · Dark", widthDp = 390, heightDp = 844)
@Composable
private fun EquipeDarkPreview() = PartejarTheme(themeMode = ThemeMode.DARK) {
    WorkspaceAdministrationLayout(previewWorkspace, 1, EquipeUiState.Content(previewMembers), EquipeActionState.Idle, null, {}, {}, {}, {}, {}, {}, {})
}

@Preview(name = "Equipe · vazio", widthDp = 390, heightDp = 844)
@Composable
private fun EquipeEmptyPreview() = PartejarTheme {
    WorkspaceAdministrationLayout(previewWorkspace, 1, EquipeUiState.Content(emptyList()), EquipeActionState.Idle, null, {}, {}, {}, {}, {}, {}, {})
}

@Preview(name = "Convidar pessoa", widthDp = 390, heightDp = 844)
@Composable
private fun InvitePreview() = PartejarTheme {
    InviteForm(EquipeActionState.Idle, {}, {}, {})
}

@Preview(name = "Editar pessoa", widthDp = 390, heightDp = 844)
@Composable
private fun EditPreview() = PartejarTheme {
    EditMemberForm(
        previewMembers.first(),
        true,
        emptyList(),
        EquipeActionState.Idle,
        BiometricAvailability.AVAILABLE,
        {},
        {},
        {},
        {}
    )
}
